import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { 
  AutocompleteContainer, 
  AutocompleteInput, 
  AutocompleteDropdown, 
  AutocompleteItem,
  AutocompleteLabel,
  AutocompleteError,
  AutocompleteLoading
} from "./AutocompleteStyles";
import CheckRender from "../../Common/CheckRender";
import Spinner from "../../Common/Spinner";
import { isEmptyValue } from "../../../utils/StringUtil";

const CustomAutocomplete = forwardRef(({
  label,
  placeholder,
  value,
  onChange,
  onSelect,
  options = [],
  isLoading = false,
  disabled = false,
  required = false,
  error = "",
  width = "100%",
  height = "40px",
  fontSize = "16px",
  borderRadius = "4px",
  maxHeight = "200px",
  noResultsText = "No se encontraron resultados",
  loadingText = "Cargando...",
  clearable = true,
  searchable = true,
  caseSensitive = false,
  minSearchLength = 0,
  displayKey = "label",
  valueKey = "value",
  className,
  style,
  onFocus,
  onBlur,
  onClear,
  testid,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSelected, setIsSelected] = useState(value ? true : false);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setIsSelected(value ? true : false);
    if (value) {
      setSearchTerm(value);
    } else {
      setSearchTerm("");
    }
  }, [value]);

  useEffect(() => {
    if (!searchable || isEmptyValue(searchTerm)) {
      setFilteredOptions(options);
      return;
    }

    if (searchTerm.length < minSearchLength) {
      setFilteredOptions([]);
      return;
    }

    const filtered = options.filter(option => {
      const displayValue = option[displayKey] || option.label || option;
      const searchValue = caseSensitive ? searchTerm : searchTerm.toLowerCase();
      const optionValue = caseSensitive ? displayValue : displayValue.toLowerCase();
      
      return optionValue.includes(searchValue);
    });

    setFilteredOptions(filtered);
  }, [searchTerm, options, searchable, caseSensitive, minSearchLength, displayKey]);

  const handleSelect = (option) => {
    const optionValue = option[valueKey] || option.value || option;
    const optionLabel = option[displayKey] || option.label || option;
    
    setSearchTerm(optionLabel);
    setIsOpen(false);
    setHighlightedIndex(-1);
    setIsSelected(true);
    
    if (onSelect) {
      onSelect(option, optionValue);
    }
    
    if (onChange) {
      onChange(optionValue);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
    setIsSelected(false);
    
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        return;
      }
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      case "Tab":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClear = () => {
    setSearchTerm("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    setIsSelected(false);
    
    if (onClear) {
      onClear();
    }
    
    if (onChange) {
      onChange("");
    }
  };

  useImperativeHandle(ref, () => ({
    clear: handleClear,
  }));

  const handleFocus = (e) => {
    if (!isSelected) {
      setIsOpen(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  const renderOptions = () => {
    if (isLoading) {
      return (
        <AutocompleteLoading>
          <Spinner size={20} />
          <span>{loadingText}</span>
        </AutocompleteLoading>
      );
    }

    if (filteredOptions.length === 0) {
      return (
        <AutocompleteItem disabled>
          {noResultsText}
        </AutocompleteItem>
      );
    }

    return filteredOptions.map((option, index) => {
      const optionLabel = option[displayKey] || option.label || option;
      const isHighlighted = index === highlightedIndex;
      
      return (
        <AutocompleteItem
          key={index}
          highlighted={isHighlighted}
          onClick={() => handleSelect(option)}
        >
          {optionLabel}
        </AutocompleteItem>
      );
    });
  };

  return (
    <AutocompleteContainer
      ref={containerRef}
      className={className}
      style={style}
      width={width}
      testid={testid}
    >
      <CheckRender allowed={label}>
        <AutocompleteLabel required={required}>
          {label}
          {required && <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>}
        </AutocompleteLabel>
      </CheckRender>

      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <AutocompleteInput
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm || value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled || isSelected}
          fontSize={fontSize}
          height={height}
          borderRadius={borderRadius}
          error={!!error}
          style={{
            paddingRight: clearable && searchTerm && !disabled ? "40px" : "10px",
            ...props.style
          }}
          {...props}
        />

        <CheckRender allowed={clearable && searchTerm && !disabled}>
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "18px",
              color: "#999",
              padding: "4px",
              zIndex: 1,
            }}
            title="Limpiar"
          >
            ×
          </button>
        </CheckRender>
      </div>

      <CheckRender allowed={isOpen && !disabled}>
        <AutocompleteDropdown
          ref={dropdownRef}
          maxHeight={maxHeight}
        >
          {renderOptions()}
        </AutocompleteDropdown>
      </CheckRender>

      <CheckRender allowed={error}>
        <AutocompleteError>
          {error}
        </AutocompleteError>
      </CheckRender>
    </AutocompleteContainer>
  );
});

CustomAutocomplete.displayName = "CustomAutocomplete";

export default CustomAutocomplete;
